import { useEffect, useRef } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { Input } from '@/components/Forms'
import { OFFICIAL_REPORT_FORM_FIELDS } from '../../constant'
import styles from '../OfficialReportModal.module.scss'
import { isHeadwayValid } from '../../helpers/isHeadwayValid'
import { ReportHeadway } from '@/types/officialReport'

const generateFormName = (index: number, key: string) => `${OFFICIAL_REPORT_FORM_FIELDS.headways}.${index}.${key}`
const DEFAULT_HEADWAY = {
    headway: '',
    busStop: '',
    observedAt: '',
}

const getLastHeadwayFieldPaths = (headways: ReportHeadway[]): string[] => {
    const lastIndex = headways.length - 1
    return [
        `${OFFICIAL_REPORT_FORM_FIELDS.headways}.${lastIndex}.headway`,
        `${OFFICIAL_REPORT_FORM_FIELDS.headways}.${lastIndex}.busStop`,
        `${OFFICIAL_REPORT_FORM_FIELDS.headways}.${lastIndex}.observedAt`,
    ]
}

export const Headway = () => {
    const init = useRef(false)
    const { control, register, getValues, clearErrors } = useFormContext()
    const { fields, append, replace, remove } = useFieldArray({
        control,
        name: OFFICIAL_REPORT_FORM_FIELDS.headways,
    })

    const handleHeadwayDataUpdate = () => {
        const { headways } = getValues()

        if (!headways.length) {
            return
        }

        const lastHeadway = headways.at(-1)
        const lastEmpty = lastHeadway && !isHeadwayValid(lastHeadway)
        const lastIdDirty = lastHeadway && Object.values(lastHeadway).some((item) => !!item)
        const emptyHeadways = (headways as ReportHeadway[]).reduce<number[]>(
            (acc, item, index) =>
                !Object.values(item).some((value) => !!value) && index !== headways.length - 1 ? [...acc, index] : acc,
            []
        )

        if (emptyHeadways.length) {
            remove(emptyHeadways)
        }

        if (lastEmpty) {
            !lastIdDirty && clearErrors(getLastHeadwayFieldPaths(headways)) // Clear last row
            return
        }

        append(DEFAULT_HEADWAY, {
            shouldFocus: false,
        })
    }

    useEffect(() => {
        if (init.current) {
            return
        }

        const values = getValues()

        if (!values?.headways.length) {
            replace(DEFAULT_HEADWAY)
        }
        init.current = true
    }, [])

    return (
        <>
            {fields.map((field, index) => (
                <div key={field.id} className={styles.fieldsRow}>
                    <Input
                        className={styles.inputField}
                        fullWidth
                        label="Headway"
                        placeholder="Headway"
                        {...register(generateFormName(index, 'headway'))}
                        onBlur={handleHeadwayDataUpdate}
                    />
                    <Input
                        className={styles.inputField}
                        fullWidth
                        label="Bus stop"
                        placeholder="Bus stop"
                        {...register(generateFormName(index, 'busStop'))}
                        onBlur={handleHeadwayDataUpdate}
                    />
                    <Input
                        className={styles.inputField}
                        fullWidth
                        label="Time observed"
                        placeholder="Time"
                        {...register(generateFormName(index, 'observedAt'))}
                        onBlur={handleHeadwayDataUpdate}
                    />
                </div>
            ))}
        </>
    )
}
